import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress, Grid, IconButton, makeStyles, Typography, useMediaQuery } from '@material-ui/core';
import { ArrowBack } from '@mui/icons-material';
import TopNav from '../../common/TopNav';
import VirtualizedTreeList, { collectExpandableNodeIds } from '../../common/VirtualizedTreeList';
import Container from '../../Elements/Container';
import useDeviceDetect from '../../../hooks/useDeviceDetect';
import useRemainingViewportHeight from '../../../hooks/useRemainingViewportHeight';
import StateTreeRow, { getStateTreeRowHeight } from './StateTreeRow';
import StateDetailSummary from './StateDetailSummary';
import StateLeftPanelSearch from './StateLeftPanelSearch';
import { getStatus, getStatusNodes } from '../../../services/meters';
import { getDeviceStatus } from '../../../services/devices';
import { returnEmpty } from '../../../helpers/common';
import StatusTabs from './StatusTabs';
import {
  buildStateFilterSections,
  filterStateTreeBySearch,
  initialStateNodeFilterState,
  normalizeStateSearchTerm,
} from './stateTreeSearch.utils';
import { useNodesTree } from '../../../hooks/useNodesTree';
import {
  borderRadiusBoxes,
  stateDetailBodyStyle,
  stateDetailPanelStyle,
  stateDetailTitleStyle,
  stateTreeScrollAreaSx,
  themePalette,
} from './stateTreeStyles';

// Devuelve la ruta de ids hasta el nodo seleccionado para rerenderizar solo la rama necesaria del arbol.
const findSelectedPathIds = (nodes, targetId) => {
  if (!targetId) return []

  for (const currentNode of nodes || []) {
    if (currentNode?.id === targetId) return [currentNode.id]

    const nestedPath = findSelectedPathIds(currentNode?.hijos, targetId)
    if (nestedPath.length > 0) return [currentNode.id, ...nestedPath]
  }

  return []
}

const areSetsEqual = (leftSet, rightSet) => {
  if (leftSet.size !== rightSet.size) return false

  for (const value of leftSet) {
    if (!rightSet.has(value)) return false
  }

  return true
}

const buildStatusByNodeIdFromTree = (nodes) => {
  const statusMap = {}

  const walkNodes = (currentNodes) => {
    ;(currentNodes || []).forEach((node) => {
      if (node?.id !== undefined) {
        statusMap[node.id] = node
      }

      walkNodes(node?.hijos)
    })
  }

  walkNodes(nodes)

  return statusMap
}

const buildStatusByNodeIdFromStatusList = (statusList) => {
  return (statusList || []).reduce((statusMap, currentStatusNode) => {
    const nodeId = currentStatusNode?.node_id ?? currentStatusNode?.id

    if (nodeId !== undefined) {
      statusMap[nodeId] = currentStatusNode
    }

    return statusMap
  }, {})
}

const extractSelectedStatusData = (payload) => {
  if (Array.isArray(payload)) {
    return payload[0] || {}
  }

  return payload || {}
}

const resolveSelectedNodeStatusKey = (node) => {
  if (node?.automate?.llave) return node.automate.llave
  if (node?.automates?.[0]?.llave) return node.automates[0].llave

  return node?.realtime?.llave || null
}

const normalizeNodeText = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()

const hasAssociatedDevice = (node) =>
  Boolean(node?.medidor || node?.automate?.llave || node?.automates?.length > 0 || node?.realtime?.llave)

const getMissingDeviceWarningMessage = (node) => {
  const normalizedLevelName = normalizeNodeText(node?.level_nombre)

  if (normalizedLevelName === "empresa" || normalizedLevelName === "zona") {
    return "El nodo seleccionado no posee un dispositivo asociado"
  }

  return "El nodo seleccionado no posee un dispositivo asociado para consultar su estado. Puede asociar un dispositivo desde la seccion de Organizacion."
}

const useStyles = makeStyles((theme) => ({
  root: {
    color: theme.palette.primary.main,
    display: "flex",
    width: "100%",
    margin: 0,
  },
  leftContainer: {
    ...stateTreeScrollAreaSx,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    margin: 0,
    paddingRight: 0,
    paddingLeft: 0,
    boxSizing: "border-box",
    "@media (max-width: 1280px)": {
      width: "100%",
      height: stateTreeScrollAreaSx.height,
      maxHeight: stateTreeScrollAreaSx.maxHeight,
    },
  },
  rightContainer: {
    ...stateDetailPanelStyle,
    margin: 0,
    borderLeft: `1px solid ${themePalette.border.main}`,
    "@media (max-width: 1280px)": {
      height: stateDetailPanelStyle.height,
      maxHeight: stateDetailPanelStyle.maxHeight,
      marginTop: theme.spacing(1.5),
      borderLeft: "none",
      borderTop: `1px solid ${themePalette.border.main}`,
    },
  },
  titleContainer: {
    ...stateDetailTitleStyle,
  },
  title: {
    fontSize: "0.9rem",
    fontWeight: 700,
    color: themePalette.text.disabled,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
  },
  rightBody: {
    ...stateDetailBodyStyle,
    "@media (max-width: 1280px)": {
      padding: "0.65rem 0.75rem",
    },
  },
  emptyState: {
    padding: theme.spacing(2),
    borderRadius: borderRadiusBoxes,
    border: `1px solid ${themePalette.border.soft}`,
    backgroundColor: themePalette.surface.soft,
    color: themePalette.text.disabled,
  },
}));

function State() {
  const { tree, loading: treeLoading } = useNodesTree();
  const classes = useStyles();
  const { isMobile } = useDeviceDetect(1279);
  const isCompactTree = useMediaQuery("(max-width:720px)");
  const [statusNodesLoading, setStatusNodesLoading] = useState(true)
  const [selectedStatusLoading, setSelectedStatusLoading] = useState(false)
  const [selectedStatusError, setSelectedStatusError] = useState(null)
  const [selectedStatusWarning, setSelectedStatusWarning] = useState(null)

  const [selectedNode, setSelectedNode] = useState({});
  const [selectedStatus, setSelectedStatus] = useState({})
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedFilters, setSelectedFilters] = useState(() =>
    Object.fromEntries(
      Object.entries(initialStateNodeFilterState).map(([key, values]) => [key, [...values]])
    )
  )
  const [filtersAnchorEl, setFiltersAnchorEl] = useState(null)

  const [showLeftBlock, setShowLeftBlock] = useState(true);
  const [showRightBlock, setShowRightBlock] = useState(true)
  const [expandedNodeIds, setExpandedNodeIds] = useState(() => new Set())
  const hasInitializedExpandedNodes = useRef(false)
  const selectedStatusRequestIdRef = useRef(0)
  const topNavRef = useRef(null)
  const { remainingHeight: contentViewportHeight } = useRemainingViewportHeight(topNavRef)

  const toNumber = (value) => {
    const parsedValue = parseFloat(String(value ?? '').replace('%', '').trim());
    return Number.isFinite(parsedValue) ? parsedValue : null;
  };

  const MAX_ACCEPTABLE_INTEGRITY = 90
  const MAX_ACCEPTABLE_DBM_WIFI = -65
  const MAX_ACCEPTABLE_DBM_MOBILE = -80

  const [statusNodes, setStatusNodes] = useState([]);
  const [statusDetails, setStatusDetails] = useState([]);

  const detailedStatusByNodeId = useMemo(() => {
    return buildStatusByNodeIdFromStatusList(statusDetails)
  }, [statusDetails])

  // Usa el payload del arbol como base y lo completa con el endpoint de status cuando ese dato llega mas tarde.
  const statusByNodeId = useMemo(() => {
    const treeStatusByNodeId = buildStatusByNodeIdFromTree(tree)

    if (Object.keys(detailedStatusByNodeId).length === 0) {
      return treeStatusByNodeId
    }

    const mergedStatusByNodeId = { ...treeStatusByNodeId }

    Object.entries(detailedStatusByNodeId).forEach(([nodeId, detailedStatus]) => {
      const currentTreeStatus = treeStatusByNodeId[nodeId] || {}

      mergedStatusByNodeId[nodeId] = {
        ...currentTreeStatus,
        ...detailedStatus,
        powermate: {
          ...(currentTreeStatus?.powermate || {}),
          ...(detailedStatus?.powermate || {}),
        },
        stats: {
          ...(currentTreeStatus?.stats || {}),
          ...(detailedStatus?.stats || {}),
        },
        wifi: {
          ...(currentTreeStatus?.wifi || {}),
          ...(detailedStatus?.wifi || {}),
        },
        mobile: {
          ...(currentTreeStatus?.mobile || {}),
          ...(detailedStatus?.mobile || {}),
        },
      }
    })

    return mergedStatusByNodeId
  }, [detailedStatusByNodeId, tree])

  // Indexa conectividad de medidores para evitar busquedas lineales por rama.
  const statusNodesByNodeId = useMemo(() => {
    return statusNodes.reduce((accumulator, currentStatusNode) => {
      if (currentStatusNode?.node_id !== undefined) {
        accumulator[currentStatusNode.node_id] = currentStatusNode
      }
      return accumulator
    }, {})
  }, [statusNodes])

  const selectedNodeId = selectedNode?.id ?? null
  const selectedPathIds = useMemo(() => new Set(findSelectedPathIds(tree, selectedNodeId)), [tree, selectedNodeId])
  const expandableNodeIds = useMemo(() => collectExpandableNodeIds(tree), [tree])
  const expandableNodeIdsSet = useMemo(() => new Set(expandableNodeIds), [expandableNodeIds])
  const normalizedSearchTerm = useMemo(() => normalizeStateSearchTerm(searchTerm), [searchTerm])
  const activeFilterCount = useMemo(
    () => Object.values(selectedFilters).reduce((total, values) => total + values.length, 0),
    [selectedFilters]
  )
  const filterSections = useMemo(() => buildStateFilterSections(tree), [tree])
  const {
    filteredTree,
    expandedSearchNodeIds,
    hasMatches: hasSearchMatches,
  } = useMemo(
    () =>
      filterStateTreeBySearch({
        nodes: tree,
        rawSearchTerm: normalizedSearchTerm,
        selectedFilters,
        statusByNodeId,
        statusNodesByNodeId,
      }),
    [normalizedSearchTerm, selectedFilters, statusByNodeId, statusNodesByNodeId, tree]
  )
  const isFilteringTree = Boolean(normalizedSearchTerm) || activeFilterCount > 0
  const visibleTree = isFilteringTree ? filteredTree : tree
  const treeOverscanPx = isMobile ? 220 : 560
  const effectiveExpandedNodeIds = useMemo(() => {
    if (!isFilteringTree) return expandedNodeIds

    return new Set([
      ...expandedNodeIds,
      ...expandedSearchNodeIds,
    ])
  }, [expandedNodeIds, expandedSearchNodeIds, isFilteringTree])
  const filterPopoverPaperStyle = useMemo(
    () =>
      isMobile
        ? {
          width: "auto",
          height: "auto",
          maxWidth: "none",
          maxHeight: "none",
          left: 0,
          right: 0,
          top: 0,
          bottom: 0,
          marginTop: 0,
          borderRadius: 0,
          border: "none",
          boxShadow: themePalette.shadow.dialogStrong,
          overflow: "hidden",
          zIndex: 2001,
          display: "flex",
          flexDirection: "column",
          boxSizing: "border-box",
        }
        : {
          width: "340px",
          maxHeight: "620px",
          marginTop: "0.2rem",
          borderRadius: "0.3rem",
          border: `1px solid ${themePalette.border.main}`,
          boxShadow: themePalette.shadow.dialog,
          overflow: "hidden",
          zIndex: 2001,
        },
    [isMobile]
  )

  // Verifica status de el medidor.
  useEffect(() => {
    let isMounted = true

    const getStatusNodesAux = async () => {
      try {
        const [onlineStatusResult, detailedStatusResult] = await Promise.allSettled([
          getStatusNodes(),
          getStatus(),
        ])

        if (!isMounted) return

        if (onlineStatusResult.status === 'fulfilled') {
          setStatusNodes(onlineStatusResult.value.data)
        } else {
          console.error(onlineStatusResult.reason)
        }

        if (detailedStatusResult.status === 'fulfilled') {
          setStatusDetails(detailedStatusResult.value.data)
        } else {
          console.error(detailedStatusResult.reason)
        }
      } finally {
        if (isMounted) {
          setStatusNodesLoading(false);
        }
      }
    }

    getStatusNodesAux()

    return () => {
      isMounted = false
    }
  }, [])

  const loading = treeLoading || statusNodesLoading

  useEffect(() => {
    if (isMobile) {
      setShowRightBlock(false)
    } else {
      setShowLeftBlock(true)
      setShowRightBlock(true)
    }
  }, [isMobile])

  // Inicializa las ramas abiertas una sola vez y conserva las decisiones del usuario cuando el arbol se actualiza.
  useEffect(() => {
    if (!hasInitializedExpandedNodes.current) {
      if (tree.length === 0) {
        setExpandedNodeIds(new Set())
        return
      }

      hasInitializedExpandedNodes.current = true
      setExpandedNodeIds(new Set(expandableNodeIds))
      return
    }

    setExpandedNodeIds((previousExpandedNodes) => {
      const nextExpandedNodes = new Set()

      expandableNodeIds.forEach((nodeId) => {
        if (previousExpandedNodes.has(nodeId)) {
          nextExpandedNodes.add(nodeId)
        }
      })

      return areSetsEqual(previousExpandedNodes, nextExpandedNodes) ? previousExpandedNodes : nextExpandedNodes
    })
  }, [expandableNodeIds, tree.length])

  // Si la seleccion llega desde otra interaccion, reabre sus ancestros para que siempre quede visible en el arbol.
  useEffect(() => {
    if (!hasInitializedExpandedNodes.current || selectedPathIds.size === 0) return

    setExpandedNodeIds((previousExpandedNodes) => {
      let didChange = false
      const nextExpandedNodes = new Set(previousExpandedNodes)

      selectedPathIds.forEach((nodeId) => {
        if (expandableNodeIdsSet.has(nodeId) && !nextExpandedNodes.has(nodeId)) {
          nextExpandedNodes.add(nodeId)
          didChange = true
        }
      })

      return didChange ? nextExpandedNodes : previousExpandedNodes
    })
  }, [expandableNodeIdsSet, selectedPathIds])

  const handleBackPageClick = useCallback(() => {
    setShowLeftBlock(true)
    setShowRightBlock(false)
    setSelectedNode({});
  }, [])

  const handleOpenFilters = useCallback((event) => {
    setFiltersAnchorEl(event.currentTarget)
  }, [])

  const handleCloseFilters = useCallback(() => {
    setFiltersAnchorEl(null)
  }, [])

  const toggleFilterValue = useCallback((sectionKey, optionValue) => {
    setSelectedFilters((previousFilters) => {
      const currentValues = previousFilters[sectionKey] || []
      const nextValues = currentValues.includes(optionValue)
        ? currentValues.filter((value) => value !== optionValue)
        : [...currentValues, optionValue]

      return {
        ...previousFilters,
        [sectionKey]: nextValues,
      }
    })
  }, [])

  const clearAllFilters = useCallback(() => {
    setSelectedFilters(
      Object.fromEntries(
        Object.keys(initialStateNodeFilterState).map((key) => [key, []])
      )
    )
  }, [])

  // Centraliza la seleccion del arbol y sincroniza el panel de detalle con la logica vigente.
  const fetchSelectedNodeStatus = useCallback(async (node) => {
    const currentRequestId = selectedStatusRequestIdRef.current + 1
    selectedStatusRequestIdRef.current = currentRequestId

    const nodeStatusKey = resolveSelectedNodeStatusKey(node)

    if (!nodeStatusKey) {
      setSelectedStatusLoading(false)
      setSelectedStatus({})
      if (!hasAssociatedDevice(node)) {
        setSelectedStatusError(null)
        setSelectedStatusWarning(getMissingDeviceWarningMessage(node))
        return
      } else {
        setSelectedStatusWarning(null)
        setSelectedStatusError("No encontramos la llave del dispositivo para consultar el estado de este nodo.")
      }
      return
    }

    setSelectedStatusLoading(true)
    setSelectedStatusError(null)
    setSelectedStatusWarning(null)

    try {
      const response = await getDeviceStatus(nodeStatusKey)

      if (selectedStatusRequestIdRef.current !== currentRequestId) return

      setSelectedStatus(extractSelectedStatusData(response.data))
    } catch (error) {
      if (selectedStatusRequestIdRef.current !== currentRequestId) return

      console.error(error)
      setSelectedStatus({})
      setSelectedStatusWarning(null)
      setSelectedStatusError("No pudimos cargar el estado detallado de este nodo.")
    } finally {
      if (selectedStatusRequestIdRef.current === currentRequestId) {
        setSelectedStatusLoading(false)
      }
    }
  }, [])

  const handleSelectedNode = useCallback((node) => {
    setSelectedNode(node);
    setSelectedStatus({})
    setSelectedStatusError(null)
    setSelectedStatusWarning(null)

    if (isMobile) {
      setShowRightBlock(true)
      setShowLeftBlock(false)
    }

    fetchSelectedNodeStatus(node)
  }, [fetchSelectedNodeStatus, isMobile]);

  // Centraliza el despliegue para poder virtualizar solo los nodos visibles del arbol.
  const handleToggleExpandedNode = useCallback((nodeId) => {
    setExpandedNodeIds((previousExpandedNodes) => {
      const nextExpandedNodes = new Set(previousExpandedNodes)

      if (nextExpandedNodes.has(nodeId)) {
        nextExpandedNodes.delete(nodeId)
      } else {
        nextExpandedNodes.add(nodeId)
      }

      return nextExpandedNodes
    })
  }, [])

  const renderTreeRow = useCallback((item) => (
    <StateTreeRow
      item={item}
      selectedNodeId={selectedNodeId}
      handleSelectedNode={handleSelectedNode}
      statusByNodeId={statusByNodeId}
      statusNodesByNodeId={statusNodesByNodeId}
      isCompact={isCompactTree}
      onToggleExpanded={handleToggleExpandedNode}
    />
  ), [handleSelectedNode, handleToggleExpandedNode, isCompactTree, selectedNodeId, statusByNodeId, statusNodesByNodeId])

  const getVirtualizedRowHeight = useCallback(
    (item) => getStateTreeRowHeight({ depth: item.depth, isCompact: isCompactTree }),
    [isCompactTree]
  )

  // Tolera los distintos formatos de fecha que llegan desde el backend sin romper el render.
  function formatDate(raw) {
    if (!raw) return '-';

    let date;
    const rawType = typeof raw

    if (raw instanceof Date) {
      date = raw;
    } else if (rawType === 'number') {
      const timestamp = raw < 1e12 ? raw * 1000 : raw
      date = new Date(timestamp);
    } else if (rawType === 'string' && raw.includes('GMT')) {
      const cleaned = raw.replace('GMT', '').trim();
      date = new Date(cleaned);
    } else {
      date = new Date(raw);
    }

    if (isNaN(date.getTime())) {
      if (rawType === 'string' || rawType === 'number') return String(raw)
      return '-'
    }

    return date.toLocaleString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  const hasSelectedNode = selectedNode?.id !== undefined && selectedNode?.id !== null
  const shouldShowLeftPanel = !isMobile || showLeftBlock
  const shouldShowRightPanel = !isMobile || showRightBlock
  const contentAreaStyle = contentViewportHeight !== null
    ? {
      height: `${contentViewportHeight}px`,
      maxHeight: `${contentViewportHeight}px`,
      overflow: 'hidden',
    }
    : { overflow: 'hidden' }

  const leftPanelStyle = {
    display: shouldShowLeftPanel ? "flex" : "none",
    height: '100%',
    maxHeight: '100%',
  }

  const rightPanelStyle = {
    display: shouldShowRightPanel ? "flex" : "none",
    height: '100%',
    maxHeight: '100%',
    minHeight: 0,
    marginTop: isMobile ? 0 : undefined,
  }

  return (
    <>
      <Box ref={topNavRef}>
        <TopNav titulo={'Dashboards'} subtitulo={"| Estado"} />
      </Box>

      <Container>
        {loading && (
          <Box display='flex' justifyContent='center' mt={2}>
            <CircularProgress />
          </Box>
        )}

        {!loading && (
          <Grid className={classes.root} container style={contentAreaStyle}>
            <Grid
              item
              xs={12}
              lg={6}
              className={classes.leftContainer}
              style={leftPanelStyle}
            >
                <StateLeftPanelSearch
                  isMobile={isMobile}
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                  activeFilterCount={activeFilterCount}
                  handleOpenFilters={handleOpenFilters}
                  filtersAnchorEl={filtersAnchorEl}
                  handleCloseFilters={handleCloseFilters}
                  filterPopoverPaperStyle={filterPopoverPaperStyle}
                  filterSections={filterSections}
                  selectedFilters={selectedFilters}
                  toggleFilterValue={toggleFilterValue}
                  clearAllFilters={clearAllFilters}
                />

                <VirtualizedTreeList
                  tree={visibleTree}
                  expandedIds={effectiveExpandedNodeIds}
                  renderRow={renderTreeRow}
                  getRowHeight={getVirtualizedRowHeight}
                  hidden={!shouldShowLeftPanel}
                  virtualization={{
                    listPaddingLeft: 12,
                    listPaddingRight: 0,
                    rowGap: isCompactTree ? 3 : 4,
                    overscanPx: treeOverscanPx,
                  }}
                  emptyState={
                    <Box className={classes.emptyState} m={2}>
                      {isFilteringTree && !hasSearchMatches
                        ? "No encontramos nodos con ese criterio de busqueda."
                        : "No hay nodos disponibles para mostrar."}
                    </Box>
                  }
                />
            </Grid>

            <Grid
              item
              xs={12}
              lg={6}
              className={classes.rightContainer}
              style={rightPanelStyle}
            >
                <Box className={classes.titleContainer}>
                  {isMobile && (
                    <IconButton onClick={handleBackPageClick} style={{ color: themePalette.primary.main }}>
                      <ArrowBack fontSize="small" />
                    </IconButton>
                  )}

                  <Typography variant='h2' className={classes.title}>
                    <Box ml={isMobile ? 0 : 1}>Estado del nodo</Box>
                  </Typography>
                </Box>

                <Box className={classes.rightBody}>
                  {hasSelectedNode ? (
                    <>
                      <StateDetailSummary
                        selectedNode={selectedNode}
                        selectedStatus={selectedStatus}
                      />

                      {selectedStatusLoading ? (
                        <Box display='flex' justifyContent='center' alignItems='center' minHeight={180}>
                          <CircularProgress size={28} />
                        </Box>
                      ) : selectedStatusWarning ? (
                        <Box className={classes.emptyState}>
                          {selectedStatusWarning}
                        </Box>
                      ) : selectedStatusError ? (
                        <Box className={classes.emptyState}>
                          {selectedStatusError}
                        </Box>
                      ) : (
                        <StatusTabs
                          selectedNode={selectedNode}
                          selectedStatus={selectedStatus}
                          formatDate={formatDate}
                          returnEmpty={returnEmpty}
                          toNumber={toNumber}
                          MAX_ACCEPTABLE_DBM_WIFI={MAX_ACCEPTABLE_DBM_WIFI}
                          MAX_ACCEPTABLE_DBM_MOBILE={MAX_ACCEPTABLE_DBM_MOBILE}
                          MAX_ACCEPTABLE_INTEGRITY={MAX_ACCEPTABLE_INTEGRITY}
                        />
                      )}
                    </>
                  ) : (
                    <Box className={classes.emptyState}>
                      Selecciona un nodo del arbol para ver su estado y detalle tecnico.
                    </Box>
                  )}
                </Box>
            </Grid>
          </Grid>
        )}
      </Container>
    </>
  )
}

export default State
